import styled from "styled-components";
import DeviceGaugeCard from "../components/DeviceGaugeCard";
import { C1Text, C3Text } from "../../../common/components/text/CustomText";
import { Row, SizedBox } from "../../../common/components/CommonBaseComponents";
import CustomImage from "../../../common/components/image/CustomImage";
import { CustomTable } from "../../../common/components/table/CustomTable";
import SearchBar from "../../../common/components/input/SearchBar";
import { useEffect, useState, useMemo } from "react";
import DeviceService from "../../../service/DeviceService";
import StoreService from "../../../service/StoreService";
import { CustomDateFilterButton } from "../../../common/components/button/CustomDateFilterButton";
import Translate from "../../../common/translate/Translate";
import { useWindowSize } from "../../../common/hooks/useWindowSize";
import ParseUtil from "../../../common/utils/ParseUtil";

const WebTextCardGrid = styled.div`
    display: grid;
    grid-template-columns: ${({ gridTemplateColumns }) => gridTemplateColumns || "repeat(3, 1fr)"};
    gap: 17px;
    width: 100%;
    height: ${({ height }) => height || "324px"};
    overflow-x: auto;
`;

// 04.01.02.22 응답(~Count) → 게이지 카드가 쓰는 필드로 매핑
const mapStatusCount = (c) => ({
    working: c?.workingCount ?? 0,
    error: c?.errorCount ?? 0,
    vacant: c?.vacantCount ?? 0,
    notAvailable: c?.notAvailableCount ?? 0,
});

function DeviceStatusSubTabContent({ shopCode }) {
    const deviceService = new DeviceService();
    const storeService = new StoreService();
    const [searchFilter, setSearchFilter] = useState("all");
    const [searchValue, setSearchValue] = useState("");
    const [appliedQuery, setAppliedQuery] = useState({ filter: "all", value: "" });
    const [dateFilter, setDateFilter] = useState("7 Days");
    const [errorList, setErrorList] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [deviceStatus, setDeviceStatus] = useState({
        washer: { working: 0, error: 0, vacant: 0, notAvailable: 0 },
        dryer: { working: 0, error: 0, vacant: 0, notAvailable: 0 },
        combo: { working: 0, error: 0, vacant: 0, notAvailable: 0 },
    });

    useEffect(() => {
        if (!shopCode) return;
        storeService
            .getStoreDeviceStatusSummary({ storeId: shopCode })
            .then((result) => {
                if (result) {
                    setDeviceStatus({
                        washer: mapStatusCount(result.washer),
                        dryer: mapStatusCount(result.dryer),
                        combo: mapStatusCount(result.combo),
                    });
                }
            })
            .catch((error) => {
                console.error("Error fetching store device status:", error);
            });
    }, [shopCode]);

    // 매장 에러 조회는 startDay/endDay 가 필수 쿼리 파라미터 (미전달 시 400)
    useEffect(() => {
        if (!shopCode) return;
        const fmt = (d) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - (dateFilter === "30 Days" ? 30 : 7));

        deviceService
            .getDeviceErrorList({ storeId: shopCode, startDay: fmt(start), endDay: fmt(end) })
            .then((result) => {
                setErrorList(result || []);
            })
            .catch((error) => {
                console.error("Error fetching device error list:", error);
            });
    }, [shopCode, dateFilter]);

    const handleSort = (key) => {
        setSortConfig((prev) =>
            prev.key === key
                ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
                : { key, direction: "asc" }
        );
    };

    const sortedErrorList = useMemo(() => {
        if (!sortConfig.key) return errorList;
        return [...errorList].sort((a, b) => {
            const av = a[sortConfig.key] ?? "";
            const bv = b[sortConfig.key] ?? "";
            if (av < bv) return sortConfig.direction === "asc" ? -1 : 1;
            if (av > bv) return sortConfig.direction === "asc" ? 1 : -1;
            return 0;
        });
    }, [errorList, sortConfig]);

    const handleSearch = () => {
        setAppliedQuery({ filter: searchFilter, value: searchValue.trim() });
    };

    // 기기명(device) / 에러코드(code) 검색 필터링
    const displayList = useMemo(() => {
        const { filter, value } = appliedQuery;
        if (!value) return sortedErrorList;
        const q = value.toLowerCase();
        return sortedErrorList.filter((item) => {
            const device = (item.device ?? "").toLowerCase();
            const code = ParseUtil.formatErrorCode(item.code).toLowerCase();
            if (filter === "device") return device.includes(q);
            if (filter === "errorCode") return code.includes(q);
            return device.includes(q) || code.includes(q); // all
        });
    }, [sortedErrorList, appliedQuery]);

    const tableHeaders = [
        {
            key: "device",
            label: Translate("store.deviceSearchType.device"),
            hasSortButton: true,
            onClick: () => handleSort("device"),
            render: (row) => (
                <Row maxWidth={"388px"}>
                    <CustomImage
                        imageName={row.device?.toLowerCase().includes("washer") ? "image_washer" : "image_dryer"}
                        width={"14px"}
                        height={"17px"}
                    />
                    <SizedBox width={"10px"} />
                    <C3Text flex={"1"} fontWeight={"500"}>
                        {row.device}
                    </C3Text>
                </Row>
            ),
        },
        {
            key: "code",
            label: Translate("store.deviceSearchType.errorCode") + " (Detail)",
            render: (row) => ParseUtil.formatErrorCode(row.code),
        },
        {
            key: "todo",
            label: Translate("store.deviceStatusTableHeader.todo"),
        },
        {
            key: "date",
            label: Translate("store.deviceStatusTableHeader.errorTime"),
            hasSortButton: true,
            onClick: () => handleSort("date"),
        },
    ];

    const { width } = useWindowSize();

    const washerGaugeCard = (
        <DeviceGaugeCard
            type={"washer"}
            workingValue={deviceStatus.washer.working}
            errorValue={deviceStatus.washer.error}
            vacantValue={deviceStatus.washer.vacant}
            notAvailableValue={deviceStatus.washer.notAvailable}
        />
    );
    const dryerGaugeCard = (
        <DeviceGaugeCard
            type={"dryer"}
            workingValue={deviceStatus.dryer.working}
            errorValue={deviceStatus.dryer.error}
            vacantValue={deviceStatus.dryer.vacant}
            notAvailableValue={deviceStatus.dryer.notAvailable}
        />
    );
    const comboGaugeCard = (
        <DeviceGaugeCard
            type={"combo"}
            workingValue={deviceStatus.combo.working}
            errorValue={deviceStatus.combo.error}
            vacantValue={deviceStatus.combo.vacant}
            notAvailableValue={deviceStatus.combo.notAvailable}
        />
    );

    let gaugeCards = (
        <WebTextCardGrid
            gridTemplateColumns={width <= 2100 ? "repeat(2, 1fr)" : "repeat(3, 1fr)"}
            height={width <= 2100 ? "665px" : "324px"}
        >
            {washerGaugeCard}
            {dryerGaugeCard}
            {comboGaugeCard}
        </WebTextCardGrid>
    );

    if (width <= 1500) {
        gaugeCards = (
            <>
                {washerGaugeCard}
                <SizedBox height={"17px"} />
                {dryerGaugeCard}
                <SizedBox height={"17px"} />
                {comboGaugeCard}
            </>
        );
    }

    const searchBar = (
        <SearchBar
            type={"deviceStatus"}
            currentValue={searchFilter}
            setValue={setSearchFilter}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            onSearchButtonClick={handleSearch}
        />
    );

    let tableHeader = (
        <Row justifyContent={"space-between"} alignItem={"flex-end"} padding={"0 12px"} width={"100%"}>
            <C1Text fontWeight={"600"}>{Translate("store.errorHistory")}</C1Text>
            <Row>
                {searchBar}
                <SizedBox width={"10px"} />
                <CustomDateFilterButton
                    currentValue={dateFilter}
                    setValue={setDateFilter}
                    openedLeftSide={true}
                    values={["7 Days", "30 Days"]}
                />
            </Row>
        </Row>
    );

    return (
        <>
            {gaugeCards}
            <SizedBox height={"31px"} />
            {tableHeader}
            <SizedBox height={"20px"} />
            <CustomTable headers={tableHeaders} data={displayList} />
            <SizedBox height={"200px"} />
        </>
    );
}

export default DeviceStatusSubTabContent;
